import { useCallback, useState } from 'react'

import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ArcadeTextArea, FieldLabel, TextArea } from '@cash-design-system/react'
import { FormBlockerElementMultilineTextInputElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

const MultilineTextArea = styled(TextArea)`
  /* stylelint-disable-next-line property-no-unknown -- experimental property */
  field-sizing: content;
  border: unset;
  background-color: unset;
  min-height: unset;

  :focus {
    border: unset;
  }

  :invalid {
    border: ${({ theme }) => `2px solid ${theme.colors.error}`};
  }
`

export const MultilineTextInputElement = ({
  formSectionIndex,
  hint_text,
  prefill_text,
  max_character_count,
  min_character_count,
  disallow_newlines,
  collapse_whitespace,
  is_required,
  label_text,
}: FormBlockerElementMultilineTextInputElement & { formSectionIndex: number }) => {
  const { register } = useFormContext()
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const getHelpText = useCallback(
    (val: string | undefined) => {
      let minHelpText, maxHelpText
      const length = val ? val.length : 0

      if (max_character_count) {
        maxHelpText = t('{length}/{max_character_count} characters', {
          context: 'help text',
          length,
          max_character_count,
        })
      }
      if (min_character_count && length < min_character_count) {
        minHelpText = t('{num} characters required', {
          context: 'help text',
          num: Number(min_character_count - length),
        })
      }
      if (min_character_count && length >= min_character_count) {
        minHelpText = undefined
      }
      return minHelpText ?? maxHelpText ?? undefined
    },
    [max_character_count, min_character_count, t]
  )

  const [helpText, setHelpText] = useState(getHelpText(prefill_text))

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (collapse_whitespace) {
        e.target.value = e.target.value.replace(/\s+/g, ' ')
      } else if (disallow_newlines) {
        e.target.value = e.target.value.replace(/[\n\r]/g, '')
      }
      setHelpText(getHelpText(e.target.value))
    },
    [collapse_whitespace, disallow_newlines, getHelpText]
  )
  if (isArcadeMigrationEnabled) {
    return (
      <FieldLabel label={label_text ?? ''}>
        <ArcadeTextArea
          placeholder={hint_text}
          helpText={helpText}
          defaultValue={prefill_text}
          maxLength={max_character_count}
          {...register(`${formSectionIndex}.multiline_text_input_result`, {
            required: is_required,
            onChange,
          })}
        />
      </FieldLabel>
    )
  }

  return (
    <MultilineTextArea
      placeholder={hint_text}
      defaultValue={prefill_text}
      maxLength={max_character_count}
      {...register(`${formSectionIndex}.multiline_text_input_result`, {
        required: is_required,
        onChange,
      })}
    />
  )
}
